<!-- Vue 3 wrapper for Bryntum SchedulerBase -->

<template>
    <div ref="refElement">

        <!-- Container for Vue components used in cells, hidden from view -->
        <div
            ref="teleportsHolder"
            style="display: none"
        >
            <!-- Vue components rendered in the grid cells -->
            <teleport
                v-for="item in teleports.values()"
                :key="item.key"
                :to="item.to"
            >
                <component
                    :is="item.is"
                    :ref="item.key"
                    :data-key="item.key"
                    :data-generation="item.generation"
                    v-bind="item.bind"
                    data-vue
                />
            </teleport>
        </div>

    </div>
</template>

<script>
import { AssignmentModel, AssignmentStore, Button, ColumnStore, Combo, CrudManager, DateField, DependencyModel, DependencyStore, DisplayField, DurationField, EventModel, EventStore, Layout, Location, Mask, Model, Popup, PresetStore, ProjectModel, Rectangle, ResourceModel, ResourceStore, ResourceTimeRangeModel, ResourceTimeRangeStore, SchedulerAssignmentModel, SchedulerAssignmentStore, SchedulerBase, SchedulerBaseFeaturesConfigType, SchedulerDependencyModel, SchedulerDependencyStore, SchedulerEventModel, SchedulerEventStore, SchedulerExportDialog, SchedulerProjectModel, SchedulerResourceModel, SchedulerResourceStore, ScrollManager, Scroller, StateProvider, Store, TextField, TimeAxis, TimeField, TimeSpan, TimelineBase, Tool, Tooltip, ViewPreset, Widget } from '@bryntum/schedulerpro';
import { getCurrentInstance, onBeforeUnmount, onMounted, ref, watch, provide } from 'vue';
import useWrapperHelper from './WrapperHelper.js';
import useWrapperHelperVue3 from './WrapperHelperVue3.js';

export default {
    name : 'BryntumSchedulerBase',

    props : {

         // Configs only
        adopt                            : { type : [HTMLElement, String], default : undefined },
        align                            : { type : [Object, String], default : undefined },
        allowCreate                      : { type : Boolean, default : undefined },
        allowOver                        : { type : Boolean, default : undefined },
        allowOverlap                     : { type : Boolean, default : undefined },
        anchor                           : { type : Boolean, default : undefined },
        anchorToTarget                   : { type : Boolean, default : undefined },
        animateRemovingRows              : { type : Boolean, default : undefined },
        ariaDescription                  : { type : String, default : undefined },
        ariaLabel                        : { type : String, default : undefined },
        autoAdjustTimeAxis               : { type : Boolean, default : undefined },
        autoClose                        : { type : Boolean, default : undefined },
        autoHeight                       : { type : Boolean, default : undefined },
        autoShow                         : { type : Boolean, default : undefined },
        autoUpdateRecord                 : { type : Boolean, default : undefined },
        bbar                             : { type : [Array, Object], default : undefined },
        bodyCls                          : { type : [String, Object], default : undefined },
        bubbleEvents                     : { type : Object, default : undefined },
        bufferCoef                       : { type : Number, default : undefined },
        bufferThreshold                  : { type : Number, default : undefined },
        callOnFunctions                  : { type : Boolean, default : undefined },
        centered                         : { type : Boolean, default : undefined },
        closable                         : { type : Boolean, default : undefined },
        closeAction                      : { type : String, default : undefined },
        collapsed                        : { type : Boolean, default : undefined },
        collapsible                      : { type : [Boolean, Object], default : undefined },
        columns                          : { type : [ColumnStore, Array, Object], default : undefined },
        config                           : { type : Object, default : undefined },
        constrainTo                      : { type : [HTMLElement, Widget, Rectangle], default : undefined },
        contentElementCls                : { type : [String, Object], default : undefined },
        contextMenuTriggerEvent          : { type : String, default : undefined },
        createEventOnDblClick            : { type : [Boolean, Object], default : undefined },
        creationTooltip                  : { type : Object, default : undefined },
        crudManagerClass                 : { type : Function, default : undefined },
        date                             : { type : Date, default : undefined },
        dateFormat                       : { type : String, default : undefined },
        defaultBindProperty              : { type : String, default : undefined },
        defaultFocus                     : { type : Function, default : undefined },
        defaultRegion                    : { type : String, default : undefined },
        defaultResourceImageName         : { type : String, default : undefined },
        defaults                         : { type : Object, default : undefined },
        descriptionRenderer              : { type : Function, default : undefined },
        deselectAllOnScheduleClick       : { type : Boolean, default : undefined },
        deselectOnClick                  : { type : Boolean, default : undefined },
        destroyStore                     : { type : Boolean, default : undefined },
        destroyStores                    : { type : Boolean, default : undefined },
        disableGridRowModelWarning       : { type : Boolean, default : undefined },
        dismissDelay                     : { type : Number, default : undefined },
        dock                             : { type : [String, Object], default : undefined },
        draggable                        : { type : [Boolean, Object], default : undefined },
        durationDisplayPrecision         : { type : [Number, Boolean], default : undefined },
        enableDeleteKey                  : { type : Boolean, default : undefined },
        enableRecurringEvents            : { type : Boolean, default : undefined },
        enableSticky                     : { type : Boolean, default : undefined },
        enableTextSelection              : { type : Boolean, default : undefined },
        endParamName                     : { type : String, default : undefined },
        eventBarTextField                : { type : String, default : undefined },
        eventBodyTemplate                : { type : Function, default : undefined },
        eventRenderer                    : { type : Function, default : undefined },
        eventRendererThisObj             : { type : Object, default : undefined },
        eventSelectionDisabled           : { type : Boolean, default : undefined },
        features                         : { type : Object, default : undefined },
        fillLastColumn                   : { type : Boolean, default : undefined },
        fixedRowHeight                   : { type : Boolean, default : undefined },
        floating                         : { type : Boolean, default : undefined },
        focusOnToFront                   : { type : Boolean, default : undefined },
        footer                           : { type : [Object, String], default : undefined },
        forElement                       : { type : HTMLElement, default : undefined },
        forSelector                      : { type : String, default : undefined },
        fullRowRefresh                   : { type : Boolean, default : undefined },
        getDateConstraints               : { type : Function, default : undefined },
        getHtml                          : { type : [Function, String], default : undefined },
        getRowHeight                     : { type : Function, default : undefined },
        header                           : { type : [String, Boolean, Object], default : undefined },
        hideAnimation                    : { type : [Boolean, Object], default : undefined },
        hideDelay                        : { type : Number, default : undefined },
        hideHorizontalScrollbar          : { type : Boolean, default : undefined },
        hideOnDelegateChange             : { type : Boolean, default : undefined },
        hideWhenEmpty                    : { type : Boolean, default : undefined },
        highlightPredecessors            : { type : Boolean, default : undefined },
        highlightSuccessors              : { type : Boolean, default : undefined },
        horizontalEventSorterFn          : { type : Function, default : undefined },
        hoverDelay                       : { type : Number, default : undefined },
        htmlCls                          : { type : [String, Object], default : undefined },
        ignoreDomEventsWhileScrolling    : { type : Boolean, default : undefined },
        ignoreParentReadOnly             : { type : Boolean, default : undefined },
        infiniteScroll                   : { type : Boolean, default : undefined },
        itemCls                          : { type : String, default : undefined },
        keyMap                           : { type : Object, default : undefined },
        lazyItems                        : { type : [Object, Array], default : undefined },
        listeners                        : { type : Object, default : undefined },
        loadingMsg                       : { type : String, default : undefined },
        loadMask                         : { type : [String, Object, null], default : undefined },
        loadMaskDefaults                 : { type : Object, default : undefined },
        loadMaskError                    : { type : [Object, Mask, Boolean], default : undefined },
        localeClass                      : { type : Function, default : undefined },
        localizable                      : { type : Boolean, default : undefined },
        localizableProperties            : { type : Array, default : undefined },
        maintainSelectionOnDatasetChange : { type : Boolean, default : undefined },
        managedEventSizing               : { type : Boolean, default : undefined },
        maskDefaults                     : { type : Object, default : undefined },
        masked                           : { type : [Boolean, String, Object], default : undefined },
        maximizable                      : { type : Boolean, default : undefined },
        maximized                        : { type : Boolean, default : undefined },
        maxTimeAxisUnit                  : { type : String, default : undefined },
        modal                            : { type : Boolean, default : undefined },
        mode                             : { type : String, default : undefined },
        monitorResize                    : { type : Boolean, default : undefined },
        mouseOffsetX                     : { type : Number, default : undefined },
        mouseOffsetY                     : { type : Number, default : undefined },
        multiEventSelect                 : { type : Boolean, default : undefined },
        namedItems                       : { type : Object, default : undefined },
        narrowEventWidth                 : { type : Number, default : undefined },
        owner                            : { type : Widget, default : undefined },
        partner                          : { type : TimelineBase, default : undefined },
        passStartEndParameters           : { type : Boolean, default : undefined },
        plugins                          : { type : Array, default : undefined },
        positioned                       : { type : Boolean, default : undefined },
        preCalculateHeightLimit          : { type : Number, default : undefined },
        preserveFocusOnDatasetChange     : { type : Boolean, default : undefined },
        preserveScrollOnDatasetChange    : { type : Boolean, default : undefined },
        preventTooltipOnTouch            : { type : Boolean, default : undefined },
        range                            : { type : String, default : undefined },
        relayStoreEvents                 : { type : Boolean, default : undefined },
        resizeToFitIncludesHeader        : { type : Boolean, default : undefined },
        resourceColumns                  : { type : Object, default : undefined },
        resourceImageExtension           : { type : String, default : undefined },
        resourceImagePath                : { type : String, default : undefined },
        responsiveLevels                 : { type : Object, default : undefined },
        ripple                           : { type : [Boolean, Object], default : undefined },
        rootElement                      : { type : ShadowRoot, default : undefined },
        scrollAction                     : { type : [String, null], default : undefined },
        scrollerClass                    : { type : Function, default : undefined },
        scrollManager                    : { type : [Object, ScrollManager], default : undefined },
        selectionMode                    : { type : Object, default : undefined },
        showAnimation                    : { type : [Boolean, Object], default : undefined },
        showCreationTooltip              : { type : Boolean, default : undefined },
        showDirty                        : { type : Boolean, default : undefined },
        showEventColorPickers            : { type : Boolean, default : undefined },
        showOnClick                      : { type : Boolean, default : undefined },
        showOnHover                      : { type : Boolean, default : undefined },
        showRecurringUI                  : { type : Boolean, default : undefined },
        showTooltip                      : { type : Boolean, default : undefined },
        showTooltipWhenDisabled          : { type : Boolean, default : undefined },
        snapRelativeToEventStartDate     : { type : Boolean, default : undefined },
        startParamName                   : { type : String, default : undefined },
        stateful                         : { type : [Boolean, Object, Array], default : undefined },
        statefulEvents                   : { type : [Object, Array], default : undefined },
        stateId                          : { type : String, default : undefined },
        stateProvider                    : { type : StateProvider, default : undefined },
        stepUnit                         : { type : String, default : undefined },
        stickyHeaders                    : { type : Boolean, default : undefined },
        strips                           : { type : Object, default : undefined },
        subGridConfigs                   : { type : Object, default : undefined },
        suppressFit                      : { type : Boolean, default : undefined },
        syncMask                         : { type : [String, Object, null], default : undefined },
        tab                              : { type : [Boolean, Object], default : undefined },
        tag                              : { type : String, default : undefined },
        tbar                             : { type : [Array, Object], default : undefined },
        terminalCls                      : { type : String, default : undefined },
        terminalSides                    : { type : Array, default : undefined },
        textAlign                        : { type : String, default : undefined },
        textContent                      : { type : Boolean, default : undefined },
        timeAxis                         : { type : [Object, TimeAxis], default : undefined },
        timeAxisColumn                   : { type : Object, default : undefined },
        trackMouse                       : { type : Boolean, default : undefined },
        trapFocus                        : { type : Boolean, default : undefined },
        triggerSelectionChangeOnRemove   : { type : Boolean, default : undefined },
        ui                               : { type : [String, Object], default : undefined },
        verticalTimeAxisColumn           : { type : Object, default : undefined },
        visibleZoomFactor                : { type : Number, default : undefined },
        weekStartDay                     : { type : Number, default : undefined },
        weight                           : { type : Number, default : undefined },
        zoomKeepsOriginalTimespan        : { type : Boolean, default : undefined },
        zoomOnMouseWheel                 : { type : Boolean, default : undefined },
        zoomOnTimeAxisDoubleClick        : { type : Boolean, default : undefined },

         // Configs and properties
        alignSelf                     : { type : String, default : undefined },
        allowDropOnEventBar           : { type : Boolean, default : undefined },
        appendTo                      : { type : [HTMLElement, String], default : undefined },
        assignments                   : { type : Array, default : undefined },
        assignmentStore               : { type : [SchedulerAssignmentStore, AssignmentStore, Object], default : undefined },
        barMargin                     : { type : Number, default : undefined },
        cls                           : { type : [String, Object], default : undefined },
        columnLines                   : { type : Boolean, default : undefined },
        content                       : { type : String, default : undefined },
        creationTooltipTemplate       : { type : Function, default : undefined },
        crudManager                   : { type : [CrudManager, Object], default : undefined },
        data                          : { type : Array, default : undefined },
        dataset                       : { type : Object, default : undefined },
        dateSeparator                 : { type : String, default : undefined },
        dependencies                  : { type : Array, default : undefined },
        dependencyStore               : { type : [SchedulerDependencyStore, DependencyStore, Object], default : undefined },
        descriptionFormat             : { type : [String, Array], default : undefined },
        disabled                      : { type : Boolean, default : undefined },
        displayDateFormat             : { type : String, default : undefined },
        emptyText                     : { type : String, default : undefined },
        enableEventAnimations         : { type : Boolean, default : undefined },
        enableUndoRedoKeys            : { type : Boolean, default : undefined },
        endDate                       : { type : [Date, String], default : undefined },
        eventColor                    : { type : Object, default : undefined },
        eventLayout                   : { type : [String, Object], default : undefined },
        events                        : { type : Array, default : undefined },
        eventStore                    : { type : [SchedulerEventStore, EventStore, Object], default : undefined },
        eventStyle                    : { type : [String, null], default : undefined },
        extraData                     : { type : [String, Number, Boolean, Object], default : undefined },
        fillTicks                     : { type : Boolean, default : undefined },
        flex                          : { type : [Number, String], default : undefined },
        forceFit                      : { type : Boolean, default : undefined },
        height                        : { type : [Number, String], default : undefined },
        hidden                        : { type : Boolean, default : undefined },
        hideFooters                   : { type : Boolean, default : undefined },
        hideHeaders                   : { type : Boolean, default : undefined },
        html                          : { type : [String, Function], default : undefined },
        id                            : { type : String, default : undefined },
        insertBefore                  : { type : [HTMLElement, String], default : undefined },
        insertFirst                   : { type : [HTMLElement, String], default : undefined },
        isEventSelectable             : { type : Function, default : undefined },
        items                         : { type : [Array, Object], default : undefined },
        layout                        : { type : [Layout, String, Object], default : undefined },
        layoutStyle                   : { type : Object, default : undefined },
        longPressTime                 : { type : Number, default : undefined },
        margin                        : { type : [Number, String], default : undefined },
        maxHeight                     : { type : [String, Number], default : undefined },
        maximizeOnMobile              : { type : [Number, String], default : undefined },
        maxWidth                      : { type : [String, Number], default : undefined },
        maxZoomLevel                  : { type : Number, default : undefined },
        milestoneAlign                : { type : String, default : undefined },
        milestoneCharWidth            : { type : Number, default : undefined },
        milestoneLayoutMode           : { type : String, default : undefined },
        milestoneTextPosition         : { type : String, default : undefined },
        minHeight                     : { type : [String, Number], default : undefined },
        minWidth                      : { type : [String, Number], default : undefined },
        minZoomLevel                  : { type : Number, default : undefined },
        overlappingEventSorter        : { type : Function, default : undefined },
        presets                       : { type : [PresetStore, Array], default : undefined },
        project                       : { type : [ProjectModel, SchedulerProjectModel, Object], default : undefined },
        readOnly                      : { type : Boolean, default : undefined },
        record                        : { type : Model, default : undefined },
        resourceMargin                : { type : Number, default : undefined },
        resources                     : { type : Array, default : undefined },
        resourceStore                 : { type : [SchedulerResourceStore, ResourceStore, Object], default : undefined },
        resourceTimeRanges            : { type : Array, default : undefined },
        resourceTimeRangeStore        : { type : [ResourceTimeRangeStore, Object], default : undefined },
        rowHeight                     : { type : Number, default : undefined },
        scrollable                    : { type : [Scroller, Boolean, Object], default : undefined },
        snap                          : { type : Boolean, default : undefined },
        startDate                     : { type : [Date, String], default : undefined },
        store                         : { type : [Store, Object], default : undefined },
        strictRecordMapping           : { type : Boolean, default : undefined },
        tickSize                      : { type : Number, default : undefined },
        timeRanges                    : { type : Array, default : undefined },
        timeRangeStore                : { type : [Store, Object], default : undefined },
        timeZone                      : { type : [String, Number], default : undefined },
        title                         : { type : String, default : undefined },
        tools                         : { type : Object, default : undefined },
        tooltip                       : { type : [String, Object], default : undefined },
        tooltipTemplate               : { type : Function, default : undefined },
        transitionDuration            : { type : Number, default : undefined },
        updateTimelineContextOnScroll : { type : Boolean, default : undefined },
        useInitialAnimation           : { type : [Boolean, String], default : undefined },
        viewPreset                    : { type : [ViewPreset, Object, String], default : undefined },
        visibleDate                   : { type : [Object, Date], default : undefined },
        width                         : { type : [Number, String], default : undefined },
        workingTime                   : { type : Object, default : undefined },
        x                             : { type : Number, default : undefined },
        y                             : { type : Number, default : undefined },

         // Properties only
        anchorSize          : { type : Array, default : undefined },
        eventColors         : { type : Array, default : undefined },
        eventStyles         : { type : Array, default : undefined },
        isEngineReady       : { type : Boolean, default : undefined },
        isSettingValues     : { type : Boolean, default : undefined },
        isValid             : { type : Boolean, default : undefined },
        scrollLeft          : { type : Number, default : undefined },
        scrollTop           : { type : Number, default : undefined },
        scrollX             : { type : Number, default : undefined },
        selectedAssignments : { type : Array, default : undefined },
        selectedCell        : { type : Location, default : undefined },
        selectedCells       : { type : Array, default : undefined },
        selectedEvents      : { type : Array, default : undefined },
        selectedRecord      : { type : Model, default : undefined },
        selectedRecords     : { type : Array, default : undefined },
        selectedRows        : { type : Array, default : undefined },
        state               : { type : Object, default : undefined },
        timeResolution      : { type : [Object, Number], default : undefined },
        type                : { type : String, default : undefined },
        values              : { type : Object, default : undefined },
        zoomLevel           : { type : Number, default : undefined },

         // Events
        onAfterDependencySave             : { type : Function, default : undefined },
        onAfterDragCreate                 : { type : Function, default : undefined },
        onAfterEventDrop                  : { type : Function, default : undefined },
        onAfterEventEdit                  : { type : Function, default : undefined },
        onAfterEventSave                  : { type : Function, default : undefined },
        onAssignmentSelectionChange       : { type : Function, default : undefined },
        onBeforeAssignmentDelete          : { type : Function, default : undefined },
        onBeforeAssignmentSelectionChange : { type : Function, default : undefined },
        onBeforeCancelCellEdit            : { type : Function, default : undefined },
        onBeforeCellDelete                : { type : Function, default : undefined },
        onBeforeCellEditStart             : { type : Function, default : undefined },
        onBeforeClose                     : { type : Function, default : undefined },
        onBeforeColumnDragStart           : { type : Function, default : undefined },
        onBeforeColumnDropFinalize        : { type : Function, default : undefined },
        onBeforeCopy                      : { type : Function, default : undefined },
        onBeforeDependencyAdd             : { type : Function, default : undefined },
        onBeforeDependencyDelete          : { type : Function, default : undefined },
        onBeforeDependencyEdit            : { type : Function, default : undefined },
        onBeforeDependencyEditShow        : { type : Function, default : undefined },
        onBeforeDependencySave            : { type : Function, default : undefined },
        onBeforeDestroy                   : { type : Function, default : undefined },
        onBeforeDragCreate                : { type : Function, default : undefined },
        onBeforeDragCreateFinalize        : { type : Function, default : undefined },
        onBeforeEventAdd                  : { type : Function, default : undefined },
        onBeforeEventDelete               : { type : Function, default : undefined },
        onBeforeEventDrag                 : { type : Function, default : undefined },
        onBeforeEventDragSelect           : { type : Function, default : undefined },
        onBeforeEventDropFinalize         : { type : Function, default : undefined },
        onBeforeEventEdit                 : { type : Function, default : undefined },
        onBeforeEventEditShow             : { type : Function, default : undefined },
        onBeforeEventResize               : { type : Function, default : undefined },
        onBeforeEventResizeFinalize       : { type : Function, default : undefined },
        onBeforeEventSave                 : { type : Function, default : undefined },
        onBeforeEventSelectionChange      : { type : Function, default : undefined },
        onBeforeFinishCellEdit            : { type : Function, default : undefined },
        onBeforeHide                      : { type : Function, default : undefined },
        onBeforePan                       : { type : Function, default : undefined },
        onBeforePaste                     : { type : Function, default : undefined },
        onBeforePdfExport                 : { type : Function, default : undefined },
        onBeforePresetChange              : { type : Function, default : undefined },
        onBeforeRenderRow                 : { type : Function, default : undefined },
        onBeforeRenderRows                : { type : Function, default : undefined },
        onBeforeSelectionChange           : { type : Function, default : undefined },
        onBeforeSetRecord                 : { type : Function, default : undefined },
        onBeforeShow                      : { type : Function, default : undefined },
        onBeforeToggleGroup               : { type : Function, default : undefined },
        onBeforeToggleNode                : { type : Function, default : undefined },
        onCancelCellEdit                  : { type : Function, default : undefined },
        onCatchAll                        : { type : Function, default : undefined },
        onCellClick                       : { type : Function, default : undefined },
        onCellContextMenu                 : { type : Function, default : undefined },
        onCellDblClick                    : { type : Function, default : undefined },
        onCellMenuBeforeShow              : { type : Function, default : undefined },
        onCellMenuItem                    : { type : Function, default : undefined },
        onCellMenuShow                    : { type : Function, default : undefined },
        onCellMenuToggleItem              : { type : Function, default : undefined },
        onCellMouseOut                    : { type : Function, default : undefined },
        onCellMouseOver                   : { type : Function, default : undefined },
        onCollapseNode                    : { type : Function, default : undefined },
        onColumnDrag                      : { type : Function, default : undefined },
        onColumnDragStart                 : { type : Function, default : undefined },
        onColumnDrop                      : { type : Function, default : undefined },
        onContextMenuItem                 : { type : Function, default : undefined },
        onContextMenuToggleItem           : { type : Function, default : undefined },
        onCopy                            : { type : Function, default : undefined },
        onDataChange                      : { type : Function, default : undefined },
        onDateRangeChange                 : { type : Function, default : undefined },
        onDependenciesDrawn               : { type : Function, default : undefined },
        onDependencyClick                 : { type : Function, default : undefined },
        onDependencyContextMenu           : { type : Function, default : undefined },
        onDependencyDblClick              : { type : Function, default : undefined },
        onDependencyMouseOut              : { type : Function, default : undefined },
        onDependencyMouseOver             : { type : Function, default : undefined },
        onDestroy                         : { type : Function, default : undefined },
        onDragCreateEnd                   : { type : Function, default : undefined },
        onDragCreateStart                 : { type : Function, default : undefined },
        onDragSelecting                   : { type : Function, default : undefined },
        onEventAutoCreated                : { type : Function, default : undefined },
        onEventClick                      : { type : Function, default : undefined },
        onEventContextMenu                : { type : Function, default : undefined },
        onEventDblClick                   : { type : Function, default : undefined },
        onEventDrag                       : { type : Function, default : undefined },
        onEventDragAbort                  : { type : Function, default : undefined },
        onEventDragModeChange             : { type : Function, default : undefined },
        onEventDragReset                  : { type : Function, default : undefined },
        onEventDragStart                  : { type : Function, default : undefined },
        onEventDrop                       : { type : Function, default : undefined },
        onEventEditBeforeSetRecord        : { type : Function, default : undefined },
        onEventKeyDown                    : { type : Function, default : undefined },
        onEventKeyUp                      : { type : Function, default : undefined },
        onEventMenuBeforeShow             : { type : Function, default : undefined },
        onEventMenuItem                   : { type : Function, default : undefined },
        onEventMenuShow                   : { type : Function, default : undefined },
        onEventMouseDown                  : { type : Function, default : undefined },
        onEventMouseEnter                 : { type : Function, default : undefined },
        onEventMouseLeave                 : { type : Function, default : undefined },
        onEventMouseOut                   : { type : Function, default : undefined },
        onEventMouseOver                  : { type : Function, default : undefined },
        onEventMouseUp                    : { type : Function, default : undefined },
        onEventPartialResize              : { type : Function, default : undefined },
        onEventResizeEnd                  : { type : Function, default : undefined },
        onEventResizeStart                : { type : Function, default : undefined },
        onEventSelectionChange            : { type : Function, default : undefined },
        onExpandNode                      : { type : Function, default : undefined },
        onFileDrop                        : { type : Function, default : undefined },
        onFinishCellEdit                  : { type : Function, default : undefined },
        onFocusIn                         : { type : Function, default : undefined },
        onFocusOut                        : { type : Function, default : undefined },
        onGridRowAbort                    : { type : Function, default : undefined },
        onGridRowBeforeDragStart          : { type : Function, default : undefined },
        onGridRowBeforeDropFinalize       : { type : Function, default : undefined },
        onGridRowDrag                     : { type : Function, default : undefined },
        onGridRowDragStart                : { type : Function, default : undefined },
        onGridRowDrop                     : { type : Function, default : undefined },
        onHeaderMenuBeforeShow            : { type : Function, default : undefined },
        onHeaderMenuItem                  : { type : Function, default : undefined },
        onHeaderMenuShow                  : { type : Function, default : undefined },
        onHeaderMenuToggleItem            : { type : Function, default : undefined },
        onHide                            : { type : Function, default : undefined },
        onMouseOut                        : { type : Function, default : undefined },
        onMouseOver                       : { type : Function, default : undefined },
        onNavigate                        : { type : Function, default : undefined },
        onPaint                           : { type : Function, default : undefined },
        onPaste                           : { type : Function, default : undefined },
        onPasteNotAllowed                 : { type : Function, default : undefined },
        onPdfExport                       : { type : Function, default : undefined },
        onPointerOver                     : { type : Function, default : undefined },
        onPresetChange                    : { type : Function, default : undefined },
        onReadOnly                        : { type : Function, default : undefined },
        onRecompose                       : { type : Function, default : undefined },
        onReleaseEvent                    : { type : Function, default : undefined },
        onRenderEvent                     : { type : Function, default : undefined },
        onRenderRow                       : { type : Function, default : undefined },
        onRenderRows                      : { type : Function, default : undefined },
        onResize                          : { type : Function, default : undefined },
        onResourceHeaderClick             : { type : Function, default : undefined },
        onResourceHeaderContextmenu       : { type : Function, default : undefined },
        onResourceHeaderDblclick          : { type : Function, default : undefined },
        onResourceMenuBeforeShow          : { type : Function, default : undefined },
        onResourceMenuItem                : { type : Function, default : undefined },
        onResourceMenuShow                : { type : Function, default : undefined },
        onResourceMenuToggleItem          : { type : Function, default : undefined },
        onResourceTimeRangeClick          : { type : Function, default : undefined },
        onResourceTimeRangeContextMenu    : { type : Function, default : undefined },
        onResourceTimeRangeDblClick       : { type : Function, default : undefined },
        onResourceTimeRangeMouseDown      : { type : Function, default : undefined },
        onResourceTimeRangeMouseOut       : { type : Function, default : undefined },
        onResourceTimeRangeMouseOver      : { type : Function, default : undefined },
        onResourceTimeRangeMouseUp        : { type : Function, default : undefined },
        onResponsive                      : { type : Function, default : undefined },
        onScheduleClick                   : { type : Function, default : undefined },
        onScheduleContextMenu             : { type : Function, default : undefined },
        onScheduleDblClick                : { type : Function, default : undefined },
        onScheduleMenuBeforeShow          : { type : Function, default : undefined },
        onScheduleMenuItem                : { type : Function, default : undefined },
        onScheduleMenuShow                : { type : Function, default : undefined },
        onScheduleMouseDown               : { type : Function, default : undefined },
        onScheduleMouseEnter              : { type : Function, default : undefined },
        onScheduleMouseLeave              : { type : Function, default : undefined },
        onScheduleMouseMove               : { type : Function, default : undefined },
        onScheduleMouseUp                 : { type : Function, default : undefined },
        onScroll                          : { type : Function, default : undefined },
        onSelectionChange                 : { type : Function, default : undefined },
        onSelectionModeChange             : { type : Function, default : undefined },
        onShow                            : { type : Function, default : undefined },
        onSplit                           : { type : Function, default : undefined },
        onSplitterCollapseClick           : { type : Function, default : undefined },
        onSplitterDragEnd                 : { type : Function, default : undefined },
        onSplitterDragStart               : { type : Function, default : undefined },
        onStartCellEdit                   : { type : Function, default : undefined },
        onSubGridCollapse                 : { type : Function, default : undefined },
        onSubGridExpand                   : { type : Function, default : undefined },
        onTimeAxisChange                  : { type : Function, default : undefined },
        onTimeAxisHeaderClick             : { type : Function, default : undefined },
        onTimeAxisHeaderContextMenu       : { type : Function, default : undefined },
        onTimeAxisHeaderDblClick          : { type : Function, default : undefined },
        onTimeAxisHeaderMenuBeforeShow    : { type : Function, default : undefined },
        onTimeAxisHeaderMenuItem          : { type : Function, default : undefined },
        onTimeAxisHeaderMenuShow          : { type : Function, default : undefined },
        onTimelineContextChange           : { type : Function, default : undefined },
        onTimelineViewportResize          : { type : Function, default : undefined },
        onTimeRangeHeaderClick            : { type : Function, default : undefined },
        onTimeRangeHeaderContextMenu      : { type : Function, default : undefined },
        onTimeRangeHeaderDblClick         : { type : Function, default : undefined },
        onTimeSelectionChange             : { type : Function, default : undefined },
        onTimeSelectionElementClick       : { type : Function, default : undefined },
        onToggleGroup                     : { type : Function, default : undefined },
        onToggleNode                      : { type : Function, default : undefined },
        onToolClick                       : { type : Function, default : undefined },
        onUnsplit                         : { type : Function, default : undefined },
        onVisibleDateRangeChange          : { type : Function, default : undefined },

         // Features
        cellCopyPasteFeature       : { type : [Object, Boolean, String], default : undefined },
        cellEditFeature            : { type : [Object, Boolean, String], default : undefined },
        cellMenuFeature            : { type : [Object, Boolean, String], default : undefined },
        cellTooltipFeature         : { type : [Object, Boolean, String], default : undefined },
        columnAutoWidthFeature     : { type : [Object, Boolean, String], default : undefined },
        columnDragToolbarFeature   : { type : [Object, Boolean, String], default : undefined },
        columnLinesFeature         : { type : [Object, Boolean, String], default : undefined },
        columnPickerFeature        : { type : [Object, Boolean, String], default : undefined },
        columnRenameFeature        : { type : [Object, Boolean, String], default : undefined },
        columnReorderFeature       : { type : [Object, Boolean, String], default : undefined },
        columnResizeFeature        : { type : [Object, Boolean, String], default : undefined },
        dependenciesFeature        : { type : [Object, Boolean, String], default : undefined },
        dependencyEditFeature      : { type : [Object, Boolean, String], default : undefined },
        eventCopyPasteFeature      : { type : [Object, Boolean, String], default : undefined },
        eventDragFeature           : { type : [Object, Boolean, String], default : undefined },
        eventDragCreateFeature     : { type : [Object, Boolean, String], default : undefined },
        eventDragSelectFeature     : { type : [Object, Boolean, String], default : undefined },
        eventEditFeature           : { type : [Object, Boolean, String], default : undefined },
        eventFilterFeature         : { type : [Object, Boolean, String], default : undefined },
        eventMenuFeature           : { type : [Object, Boolean, String], default : undefined },
        eventNonWorkingTimeFeature : { type : [Object, Boolean, String], default : undefined },
        eventResizeFeature         : { type : [Object, Boolean, String], default : undefined },
        eventTooltipFeature        : { type : [Object, Boolean, String], default : undefined },
        excelExporterFeature       : { type : [Object, Boolean, String], default : undefined },
        fileDropFeature            : { type : [Object, Boolean, String], default : undefined },
        fillHandleFeature          : { type : [Object, Boolean, String], default : undefined },
        filterFeature              : { type : [Object, Boolean, String], default : undefined },
        filterBarFeature           : { type : [Object, Boolean, String], default : undefined },
        groupFeature               : { type : [Object, Boolean, String], default : undefined },
        groupSummaryFeature        : { type : [Object, Boolean, String], default : undefined },
        headerMenuFeature          : { type : [Object, Boolean, String], default : undefined },
        headerZoomFeature          : { type : [Object, Boolean, String], default : undefined },
        labelsFeature              : { type : [Object, Boolean, String], default : undefined },
        mergeCellsFeature          : { type : [Object, Boolean, String], default : undefined },
        multipageFeature           : { type : [Object, Boolean, String], default : undefined },
        multipageverticalFeature   : { type : [Object, Boolean, String], default : undefined },
        nonWorkingTimeFeature      : { type : [Object, Boolean, String], default : undefined },
        panFeature                 : { type : [Object, Boolean, String], default : undefined },
        pdfExportFeature           : { type : [Object, Boolean, String], default : undefined },
        quickFindFeature           : { type : [Object, Boolean, String], default : undefined },
        regionResizeFeature        : { type : [Object, Boolean, String], default : undefined },
        resourceMenuFeature        : { type : [Object, Boolean, String], default : undefined },
        resourceTimeRangesFeature  : { type : [Object, Boolean, String], default : undefined },
        rowCopyPasteFeature        : { type : [Object, Boolean, String], default : undefined },
        rowExpanderFeature         : { type : [Object, Boolean, String], default : undefined },
        rowReorderFeature          : { type : [Object, Boolean, String], default : undefined },
        scheduleContextFeature     : { type : [Object, Boolean, String], default : undefined },
        scheduleMenuFeature        : { type : [Object, Boolean, String], default : undefined },
        scheduleTooltipFeature     : { type : [Object, Boolean, String], default : undefined },
        searchFeature              : { type : [Object, Boolean, String], default : undefined },
        simpleEventEditFeature     : { type : [Object, Boolean, String], default : undefined },
        singlepageFeature          : { type : [Object, Boolean, String], default : undefined },
        sortFeature                : { type : [Object, Boolean, String], default : undefined },
        splitFeature               : { type : [Object, Boolean, String], default : undefined },
        stickyCellsFeature         : { type : [Object, Boolean, String], default : undefined },
        stickyEventsFeature        : { type : [Object, Boolean, String], default : undefined },
        stripeFeature              : { type : [Object, Boolean, String], default : undefined },
        summaryFeature             : { type : [Object, Boolean, String], default : undefined },
        timeAxisHeaderMenuFeature  : { type : [Object, Boolean, String], default : undefined },
        timeRangesFeature          : { type : [Object, Boolean, String], default : undefined },
        timeSelectionFeature       : { type : [Object, Boolean, String], default : undefined },
        treeFeature                : { type : [Object, Boolean, String], default : undefined },
        treeGroupFeature           : { type : [Object, Boolean, String], default : undefined },

    },

    emits : [
        'afterdependencysave',
        'afterdragcreate',
        'aftereventdrop',
        'aftereventedit',
        'aftereventsave',
        'assignmentselectionchange',
        'beforeassignmentdelete',
        'beforeassignmentselectionchange',
        'beforecancelcelledit',
        'beforecelldelete',
        'beforecelleditstart',
        'beforeclose',
        'beforecolumndragstart',
        'beforecolumndropfinalize',
        'beforecopy',
        'beforedependencyadd',
        'beforedependencydelete',
        'beforedependencyedit',
        'beforedependencyeditshow',
        'beforedependencysave',
        'beforedestroy',
        'beforedragcreate',
        'beforedragcreatefinalize',
        'beforeeventadd',
        'beforeeventdelete',
        'beforeeventdrag',
        'beforeeventdragselect',
        'beforeeventdropfinalize',
        'beforeeventedit',
        'beforeeventeditshow',
        'beforeeventresize',
        'beforeeventresizefinalize',
        'beforeeventsave',
        'beforeeventselectionchange',
        'beforefinishcelledit',
        'beforehide',
        'beforepan',
        'beforepaste',
        'beforepdfexport',
        'beforepresetchange',
        'beforerenderrow',
        'beforerenderrows',
        'beforeselectionchange',
        'beforesetrecord',
        'beforeshow',
        'beforetogglegroup',
        'beforetogglenode',
        'cancelcelledit',
        'catchall',
        'cellclick',
        'cellcontextmenu',
        'celldblclick',
        'cellmenubeforeshow',
        'cellmenuitem',
        'cellmenushow',
        'cellmenutoggleitem',
        'cellmouseout',
        'cellmouseover',
        'collapsenode',
        'columndrag',
        'columndragstart',
        'columndrop',
        'contextmenuitem',
        'contextmenutoggleitem',
        'copy',
        'datachange',
        'daterangechange',
        'dependenciesdrawn',
        'dependencyclick',
        'dependencycontextmenu',
        'dependencydblclick',
        'dependencymouseout',
        'dependencymouseover',
        'destroy',
        'dragcreateend',
        'dragcreatestart',
        'dragselecting',
        'eventautocreated',
        'eventclick',
        'eventcontextmenu',
        'eventdblclick',
        'eventdrag',
        'eventdragabort',
        'eventdragmodechange',
        'eventdragreset',
        'eventdragstart',
        'eventdrop',
        'eventeditbeforesetrecord',
        'eventkeydown',
        'eventkeyup',
        'eventmenubeforeshow',
        'eventmenuitem',
        'eventmenushow',
        'eventmousedown',
        'eventmouseenter',
        'eventmouseleave',
        'eventmouseout',
        'eventmouseover',
        'eventmouseup',
        'eventpartialresize',
        'eventresizeend',
        'eventresizestart',
        'eventselectionchange',
        'expandnode',
        'filedrop',
        'finishcelledit',
        'focusin',
        'focusout',
        'gridrowabort',
        'gridrowbeforedragstart',
        'gridrowbeforedropfinalize',
        'gridrowdrag',
        'gridrowdragstart',
        'gridrowdrop',
        'headermenubeforeshow',
        'headermenuitem',
        'headermenushow',
        'headermenutoggleitem',
        'hide',
        'mouseout',
        'mouseover',
        'navigate',
        'paint',
        'paste',
        'pastenotallowed',
        'pdfexport',
        'pointerover',
        'presetchange',
        'recompose',
        'releaseevent',
        'renderevent',
        'renderrow',
        'renderrows',
        'resize',
        'resourceheaderclick',
        'resourceheadercontextmenu',
        'resourceheaderdblclick',
        'resourcemenubeforeshow',
        'resourcemenuitem',
        'resourcemenushow',
        'resourcemenutoggleitem',
        'resourcetimerangeclick',
        'resourcetimerangecontextmenu',
        'resourcetimerangedblclick',
        'resourcetimerangemousedown',
        'resourcetimerangemouseout',
        'resourcetimerangemouseover',
        'resourcetimerangemouseup',
        'responsive',
        'scheduleclick',
        'schedulecontextmenu',
        'scheduledblclick',
        'schedulemenubeforeshow',
        'schedulemenuitem',
        'schedulemenushow',
        'schedulemousedown',
        'schedulemouseenter',
        'schedulemouseleave',
        'schedulemousemove',
        'schedulemouseup',
        'scroll',
        'selectionchange',
        'selectionmodechange',
        'show',
        'split',
        'splittercollapseclick',
        'splitterdragend',
        'splitterdragstart',
        'startcelledit',
        'subgridcollapse',
        'subgridexpand',
        'timeaxischange',
        'timeaxisheaderclick',
        'timeaxisheadercontextmenu',
        'timeaxisheaderdblclick',
        'timeaxisheadermenubeforeshow',
        'timeaxisheadermenuitem',
        'timeaxisheadermenushow',
        'timelinecontextchange',
        'timelineviewportresize',
        'timerangeheaderclick',
        'timerangeheadercontextmenu',
        'timerangeheaderdblclick',
        'timeselectionchange',
        'timeselectionelementclick',
        'togglegroup',
        'togglenode',
        'toolclick',
        'unsplit',
        'visibledaterangechange'
    ],

    data : () => {
        return {
            instanceClass : SchedulerBase,
            instanceName : 'SchedulerBase',

            isView : true,

            dataStores : {
                'assignmentStore': 'assignments',
                'dependencyStore': 'dependencies',
                'eventStore': 'events',
                'resourceStore': 'resources',
                'resourceTimeRangeStore': 'resourceTimeRanges',
                'timeRangeStore': 'timeRanges'
            },

            projectStores : false,

            featureNames : [
                'cellCopyPasteFeature',
                'cellEditFeature',
                'cellMenuFeature',
                'cellTooltipFeature',
                'columnAutoWidthFeature',
                'columnDragToolbarFeature',
                'columnLinesFeature',
                'columnPickerFeature',
                'columnRenameFeature',
                'columnReorderFeature',
                'columnResizeFeature',
                'dependenciesFeature',
                'dependencyEditFeature',
                'eventCopyPasteFeature',
                'eventDragFeature',
                'eventDragCreateFeature',
                'eventDragSelectFeature',
                'eventEditFeature',
                'eventFilterFeature',
                'eventMenuFeature',
                'eventNonWorkingTimeFeature',
                'eventResizeFeature',
                'eventTooltipFeature',
                'excelExporterFeature',
                'fileDropFeature',
                'fillHandleFeature',
                'filterFeature',
                'filterBarFeature',
                'groupFeature',
                'groupSummaryFeature',
                'headerMenuFeature',
                'headerZoomFeature',
                'labelsFeature',
                'mergeCellsFeature',
                'multipageFeature',
                'multipageverticalFeature',
                'nonWorkingTimeFeature',
                'panFeature',
                'pdfExportFeature',
                'quickFindFeature',
                'regionResizeFeature',
                'resourceMenuFeature',
                'resourceTimeRangesFeature',
                'rowCopyPasteFeature',
                'rowExpanderFeature',
                'rowReorderFeature',
                'scheduleContextFeature',
                'scheduleMenuFeature',
                'scheduleTooltipFeature',
                'searchFeature',
                'simpleEventEditFeature',
                'singlepageFeature',
                'sortFeature',
                'splitFeature',
                'stickyCellsFeature',
                'stickyEventsFeature',
                'stripeFeature',
                'summaryFeature',
                'timeAxisHeaderMenuFeature',
                'timeRangesFeature',
                'timeSelectionFeature',
                'treeFeature',
                'treeGroupFeature'
            ],

            configNames : [
                'adopt',
                'align',
                'allowCreate',
                'allowOver',
                'allowOverlap',
                'anchor',
                'anchorToTarget',
                'animateRemovingRows',
                'ariaDescription',
                'ariaLabel',
                'autoAdjustTimeAxis',
                'autoClose',
                'autoHeight',
                'autoShow',
                'autoUpdateRecord',
                'bbar',
                'bodyCls',
                'bubbleEvents',
                'bufferCoef',
                'bufferThreshold',
                'callOnFunctions',
                'centered',
                'closable',
                'closeAction',
                'collapsed',
                'collapsible',
                'columns',
                'config',
                'constrainTo',
                'contentElementCls',
                'contextMenuTriggerEvent',
                'createEventOnDblClick',
                'creationTooltip',
                'crudManagerClass',
                'date',
                'dateFormat',
                'defaultBindProperty',
                'defaultFocus',
                'defaultRegion',
                'defaultResourceImageName',
                'defaults',
                'descriptionRenderer',
                'deselectAllOnScheduleClick',
                'deselectOnClick',
                'destroyStore',
                'destroyStores',
                'disableGridRowModelWarning',
                'dismissDelay',
                'dock',
                'draggable',
                'durationDisplayPrecision',
                'enableDeleteKey',
                'enableRecurringEvents',
                'enableSticky',
                'enableTextSelection',
                'endParamName',
                'eventBarTextField',
                'eventBodyTemplate',
                'eventRenderer',
                'eventRendererThisObj',
                'eventSelectionDisabled',
                'features',
                'fillLastColumn',
                'fixedRowHeight',
                'floating',
                'focusOnToFront',
                'footer',
                'forElement',
                'forSelector',
                'fullRowRefresh',
                'getDateConstraints',
                'getHtml',
                'getRowHeight',
                'header',
                'hideAnimation',
                'hideDelay',
                'hideHorizontalScrollbar',
                'hideOnDelegateChange',
                'hideWhenEmpty',
                'highlightPredecessors',
                'highlightSuccessors',
                'horizontalEventSorterFn',
                'hoverDelay',
                'htmlCls',
                'ignoreDomEventsWhileScrolling',
                'ignoreParentReadOnly',
                'infiniteScroll',
                'itemCls',
                'keyMap',
                'lazyItems',
                'listeners',
                'loadingMsg',
                'loadMask',
                'loadMaskDefaults',
                'loadMaskError',
                'localeClass',
                'localizable',
                'localizableProperties',
                'maintainSelectionOnDatasetChange',
                'managedEventSizing',
                'maskDefaults',
                'masked',
                'maximizable',
                'maximized',
                'maxTimeAxisUnit',
                'modal',
                'mode',
                'monitorResize',
                'mouseOffsetX',
                'mouseOffsetY',
                'multiEventSelect',
                'namedItems',
                'narrowEventWidth',
                'owner',
                'partner',
                'passStartEndParameters',
                'plugins',
                'positioned',
                'preCalculateHeightLimit',
                'preserveFocusOnDatasetChange',
                'preserveScrollOnDatasetChange',
                'preventTooltipOnTouch',
                'range',
                'relayStoreEvents',
                'resizeToFitIncludesHeader',
                'resourceColumns',
                'resourceImageExtension',
                'resourceImagePath',
                'responsiveLevels',
                'ripple',
                'rootElement',
                'scrollAction',
                'scrollerClass',
                'scrollManager',
                'selectionMode',
                'showAnimation',
                'showCreationTooltip',
                'showDirty',
                'showEventColorPickers',
                'showOnClick',
                'showOnHover',
                'showRecurringUI',
                'showTooltip',
                'showTooltipWhenDisabled',
                'snapRelativeToEventStartDate',
                'startParamName',
                'stateful',
                'statefulEvents',
                'stateId',
                'stateProvider',
                'stepUnit',
                'stickyHeaders',
                'strips',
                'subGridConfigs',
                'suppressFit',
                'syncMask',
                'tab',
                'tag',
                'tbar',
                'terminalCls',
                'terminalSides',
                'textAlign',
                'textContent',
                'timeAxis',
                'timeAxisColumn',
                'trackMouse',
                'trapFocus',
                'triggerSelectionChangeOnRemove',
                'ui',
                'verticalTimeAxisColumn',
                'visibleZoomFactor',
                'weekStartDay',
                'weight',
                'zoomKeepsOriginalTimespan',
                'zoomOnMouseWheel',
                'zoomOnTimeAxisDoubleClick'
            ],

            propertyConfigNames : [
                'alignSelf',
                'allowDropOnEventBar',
                'appendTo',
                'assignments',
                'assignmentStore',
                'barMargin',
                'cls',
                'columnLines',
                'content',
                'creationTooltipTemplate',
                'crudManager',
                'data',
                'dataset',
                'dateSeparator',
                'dependencies',
                'dependencyStore',
                'descriptionFormat',
                'disabled',
                'displayDateFormat',
                'emptyText',
                'enableEventAnimations',
                'enableUndoRedoKeys',
                'endDate',
                'eventColor',
                'eventLayout',
                'events',
                'eventStore',
                'eventStyle',
                'extraData',
                'fillTicks',
                'flex',
                'forceFit',
                'height',
                'hidden',
                'hideFooters',
                'hideHeaders',
                'html',
                'id',
                'insertBefore',
                'insertFirst',
                'isEventSelectable',
                'items',
                'layout',
                'layoutStyle',
                'longPressTime',
                'margin',
                'maxHeight',
                'maximizeOnMobile',
                'maxWidth',
                'maxZoomLevel',
                'milestoneAlign',
                'milestoneCharWidth',
                'milestoneLayoutMode',
                'milestoneTextPosition',
                'minHeight',
                'minWidth',
                'minZoomLevel',
                'onAfterDependencySave',
                'onAfterDragCreate',
                'onAfterEventDrop',
                'onAfterEventEdit',
                'onAfterEventSave',
                'onAssignmentSelectionChange',
                'onBeforeAssignmentDelete',
                'onBeforeAssignmentSelectionChange',
                'onBeforeCancelCellEdit',
                'onBeforeCellDelete',
                'onBeforeCellEditStart',
                'onBeforeClose',
                'onBeforeColumnDragStart',
                'onBeforeColumnDropFinalize',
                'onBeforeCopy',
                'onBeforeDependencyAdd',
                'onBeforeDependencyDelete',
                'onBeforeDependencyEdit',
                'onBeforeDependencyEditShow',
                'onBeforeDependencySave',
                'onBeforeDestroy',
                'onBeforeDragCreate',
                'onBeforeDragCreateFinalize',
                'onBeforeEventAdd',
                'onBeforeEventDelete',
                'onBeforeEventDrag',
                'onBeforeEventDragSelect',
                'onBeforeEventDropFinalize',
                'onBeforeEventEdit',
                'onBeforeEventEditShow',
                'onBeforeEventResize',
                'onBeforeEventResizeFinalize',
                'onBeforeEventSave',
                'onBeforeEventSelectionChange',
                'onBeforeFinishCellEdit',
                'onBeforeHide',
                'onBeforePan',
                'onBeforePaste',
                'onBeforePdfExport',
                'onBeforePresetChange',
                'onBeforeRenderRow',
                'onBeforeRenderRows',
                'onBeforeSelectionChange',
                'onBeforeSetRecord',
                'onBeforeShow',
                'onBeforeToggleGroup',
                'onBeforeToggleNode',
                'onCancelCellEdit',
                'onCatchAll',
                'onCellClick',
                'onCellContextMenu',
                'onCellDblClick',
                'onCellMenuBeforeShow',
                'onCellMenuItem',
                'onCellMenuShow',
                'onCellMenuToggleItem',
                'onCellMouseOut',
                'onCellMouseOver',
                'onCollapseNode',
                'onColumnDrag',
                'onColumnDragStart',
                'onColumnDrop',
                'onContextMenuItem',
                'onContextMenuToggleItem',
                'onCopy',
                'onDataChange',
                'onDateRangeChange',
                'onDependenciesDrawn',
                'onDependencyClick',
                'onDependencyContextMenu',
                'onDependencyDblClick',
                'onDependencyMouseOut',
                'onDependencyMouseOver',
                'onDestroy',
                'onDragCreateEnd',
                'onDragCreateStart',
                'onDragSelecting',
                'onEventAutoCreated',
                'onEventClick',
                'onEventContextMenu',
                'onEventDblClick',
                'onEventDrag',
                'onEventDragAbort',
                'onEventDragModeChange',
                'onEventDragReset',
                'onEventDragStart',
                'onEventDrop',
                'onEventEditBeforeSetRecord',
                'onEventKeyDown',
                'onEventKeyUp',
                'onEventMenuBeforeShow',
                'onEventMenuItem',
                'onEventMenuShow',
                'onEventMouseDown',
                'onEventMouseEnter',
                'onEventMouseLeave',
                'onEventMouseOut',
                'onEventMouseOver',
                'onEventMouseUp',
                'onEventPartialResize',
                'onEventResizeEnd',
                'onEventResizeStart',
                'onEventSelectionChange',
                'onExpandNode',
                'onFileDrop',
                'onFinishCellEdit',
                'onFocusIn',
                'onFocusOut',
                'onGridRowAbort',
                'onGridRowBeforeDragStart',
                'onGridRowBeforeDropFinalize',
                'onGridRowDrag',
                'onGridRowDragStart',
                'onGridRowDrop',
                'onHeaderMenuBeforeShow',
                'onHeaderMenuItem',
                'onHeaderMenuShow',
                'onHeaderMenuToggleItem',
                'onHide',
                'onMouseOut',
                'onMouseOver',
                'onNavigate',
                'onPaint',
                'onPaste',
                'onPasteNotAllowed',
                'onPdfExport',
                'onPointerOver',
                'onPresetChange',
                'onReadOnly',
                'onRecompose',
                'onReleaseEvent',
                'onRenderEvent',
                'onRenderRow',
                'onRenderRows',
                'onResize',
                'onResourceHeaderClick',
                'onResourceHeaderContextmenu',
                'onResourceHeaderDblclick',
                'onResourceMenuBeforeShow',
                'onResourceMenuItem',
                'onResourceMenuShow',
                'onResourceMenuToggleItem',
                'onResourceTimeRangeClick',
                'onResourceTimeRangeContextMenu',
                'onResourceTimeRangeDblClick',
                'onResourceTimeRangeMouseDown',
                'onResourceTimeRangeMouseOut',
                'onResourceTimeRangeMouseOver',
                'onResourceTimeRangeMouseUp',
                'onResponsive',
                'onScheduleClick',
                'onScheduleContextMenu',
                'onScheduleDblClick',
                'onScheduleMenuBeforeShow',
                'onScheduleMenuItem',
                'onScheduleMenuShow',
                'onScheduleMouseDown',
                'onScheduleMouseEnter',
                'onScheduleMouseLeave',
                'onScheduleMouseMove',
                'onScheduleMouseUp',
                'onScroll',
                'onSelectionChange',
                'onSelectionModeChange',
                'onShow',
                'onSplit',
                'onSplitterCollapseClick',
                'onSplitterDragEnd',
                'onSplitterDragStart',
                'onStartCellEdit',
                'onSubGridCollapse',
                'onSubGridExpand',
                'onTimeAxisChange',
                'onTimeAxisHeaderClick',
                'onTimeAxisHeaderContextMenu',
                'onTimeAxisHeaderDblClick',
                'onTimeAxisHeaderMenuBeforeShow',
                'onTimeAxisHeaderMenuItem',
                'onTimeAxisHeaderMenuShow',
                'onTimelineContextChange',
                'onTimelineViewportResize',
                'onTimeRangeHeaderClick',
                'onTimeRangeHeaderContextMenu',
                'onTimeRangeHeaderDblClick',
                'onTimeSelectionChange',
                'onTimeSelectionElementClick',
                'onToggleGroup',
                'onToggleNode',
                'onToolClick',
                'onUnsplit',
                'onVisibleDateRangeChange',
                'overlappingEventSorter',
                'presets',
                'project',
                'readOnly',
                'record',
                'resourceMargin',
                'resources',
                'resourceStore',
                'resourceTimeRanges',
                'resourceTimeRangeStore',
                'rowHeight',
                'scrollable',
                'snap',
                'startDate',
                'store',
                'strictRecordMapping',
                'tickSize',
                'timeRanges',
                'timeRangeStore',
                'timeZone',
                'title',
                'tools',
                'tooltip',
                'tooltipTemplate',
                'transitionDuration',
                'updateTimelineContextOnScroll',
                'useInitialAnimation',
                'viewPreset',
                'visibleDate',
                'width',
                'workingTime',
                'x',
                'y'
            ],

            propertyNames : [
                'anchorSize',
                'eventColors',
                'eventStyles',
                'isEngineReady',
                'isSettingValues',
                'isValid',
                'scrollLeft',
                'scrollTop',
                'scrollX',
                'selectedAssignments',
                'selectedCell',
                'selectedCells',
                'selectedEvents',
                'selectedRecord',
                'selectedRecords',
                'selectedRows',
                'state',
                'timeResolution',
                'type',
                'values',
                'zoomLevel'
            ],

            eventNames : [
                'afterDependencySave',
                'afterDragCreate',
                'afterEventDrop',
                'afterEventEdit',
                'afterEventSave',
                'assignmentSelectionChange',
                'beforeAssignmentDelete',
                'beforeAssignmentSelectionChange',
                'beforeCancelCellEdit',
                'beforeCellDelete',
                'beforeCellEditStart',
                'beforeClose',
                'beforeColumnDragStart',
                'beforeColumnDropFinalize',
                'beforeCopy',
                'beforeDependencyAdd',
                'beforeDependencyDelete',
                'beforeDependencyEdit',
                'beforeDependencyEditShow',
                'beforeDependencySave',
                'beforeDestroy',
                'beforeDragCreate',
                'beforeDragCreateFinalize',
                'beforeEventAdd',
                'beforeEventDelete',
                'beforeEventDrag',
                'beforeEventDragSelect',
                'beforeEventDropFinalize',
                'beforeEventEdit',
                'beforeEventEditShow',
                'beforeEventResize',
                'beforeEventResizeFinalize',
                'beforeEventSave',
                'beforeEventSelectionChange',
                'beforeFinishCellEdit',
                'beforeHide',
                'beforePan',
                'beforePaste',
                'beforePdfExport',
                'beforePresetChange',
                'beforeRenderRow',
                'beforeRenderRows',
                'beforeSelectionChange',
                'beforeSetRecord',
                'beforeShow',
                'beforeToggleGroup',
                'beforeToggleNode',
                'cancelCellEdit',
                'catchAll',
                'cellClick',
                'cellContextMenu',
                'cellDblClick',
                'cellMenuBeforeShow',
                'cellMenuItem',
                'cellMenuShow',
                'cellMenuToggleItem',
                'cellMouseOut',
                'cellMouseOver',
                'collapseNode',
                'columnDrag',
                'columnDragStart',
                'columnDrop',
                'contextMenuItem',
                'contextMenuToggleItem',
                'copy',
                'dataChange',
                'dateRangeChange',
                'dependenciesDrawn',
                'dependencyClick',
                'dependencyContextMenu',
                'dependencyDblClick',
                'dependencyMouseOut',
                'dependencyMouseOver',
                'destroy',
                'dragCreateEnd',
                'dragCreateStart',
                'dragSelecting',
                'eventAutoCreated',
                'eventClick',
                'eventContextMenu',
                'eventDblClick',
                'eventDrag',
                'eventDragAbort',
                'eventDragModeChange',
                'eventDragReset',
                'eventDragStart',
                'eventDrop',
                'eventEditBeforeSetRecord',
                'eventKeyDown',
                'eventKeyUp',
                'eventMenuBeforeShow',
                'eventMenuItem',
                'eventMenuShow',
                'eventMouseDown',
                'eventMouseEnter',
                'eventMouseLeave',
                'eventMouseOut',
                'eventMouseOver',
                'eventMouseUp',
                'eventPartialResize',
                'eventResizeEnd',
                'eventResizeStart',
                'eventSelectionChange',
                'expandNode',
                'fileDrop',
                'finishCellEdit',
                'focusIn',
                'focusOut',
                'gridRowAbort',
                'gridRowBeforeDragStart',
                'gridRowBeforeDropFinalize',
                'gridRowDrag',
                'gridRowDragStart',
                'gridRowDrop',
                'headerMenuBeforeShow',
                'headerMenuItem',
                'headerMenuShow',
                'headerMenuToggleItem',
                'hide',
                'mouseOut',
                'mouseOver',
                'navigate',
                'paint',
                'paste',
                'pasteNotAllowed',
                'pdfExport',
                'pointerOver',
                'presetChange',
                'recompose',
                'releaseEvent',
                'renderEvent',
                'renderRow',
                'renderRows',
                'resize',
                'resourceHeaderClick',
                'resourceHeaderContextmenu',
                'resourceHeaderDblclick',
                'resourceMenuBeforeShow',
                'resourceMenuItem',
                'resourceMenuShow',
                'resourceMenuToggleItem',
                'resourceTimeRangeClick',
                'resourceTimeRangeContextMenu',
                'resourceTimeRangeDblClick',
                'resourceTimeRangeMouseDown',
                'resourceTimeRangeMouseOut',
                'resourceTimeRangeMouseOver',
                'resourceTimeRangeMouseUp',
                'responsive',
                'scheduleClick',
                'scheduleContextMenu',
                'scheduleDblClick',
                'scheduleMenuBeforeShow',
                'scheduleMenuItem',
                'scheduleMenuShow',
                'scheduleMouseDown',
                'scheduleMouseEnter',
                'scheduleMouseLeave',
                'scheduleMouseMove',
                'scheduleMouseUp',
                'scroll',
                'selectionChange',
                'selectionModeChange',
                'show',
                'split',
                'splitterCollapseClick',
                'splitterDragEnd',
                'splitterDragStart',
                'startCellEdit',
                'subGridCollapse',
                'subGridExpand',
                'timeAxisChange',
                'timeAxisHeaderClick',
                'timeAxisHeaderContextMenu',
                'timeAxisHeaderDblClick',
                'timeAxisHeaderMenuBeforeShow',
                'timeAxisHeaderMenuItem',
                'timeAxisHeaderMenuShow',
                'timelineContextChange',
                'timelineViewportResize',
                'timeRangeHeaderClick',
                'timeRangeHeaderContextMenu',
                'timeRangeHeaderDblClick',
                'timeSelectionChange',
                'timeSelectionElementClick',
                'toggleGroup',
                'toggleNode',
                'toolClick',
                'unsplit',
                'visibleDateRangeChange'
            ]
        };
    },

    setup(props, { emit }) {
        const instance = {};
        const refElement = ref(null);

        // Storage for teleports (in-cell Vue component instances)
        // automatically renderer by template
        const teleports = ref(new Map());

        const { createWidget, relayStores, watchProps } = useWrapperHelper();
        const { processCellContent, hasFrameworkRenderer } = useWrapperHelperVue3();

        // Provide teleports for processCellContent
        provide('teleports', teleports);

        onMounted(() => {
            const
                me = getCurrentInstance(),
                uncapitalize = str => str.charAt(0).toLowerCase() + str.slice(1),
                eventName = str => uncapitalize(str.slice(2)),
                // Collect event listeners from me.props where they are `on` functions
                listeners = Object.keys(me.props)
                    .filter(prop => typeof me.props[prop] === 'function' && prop.startsWith('on') && prop.length > 2)
                    .map(eventName);

            instance.value = createWidget({
                me,
                props   : me.props,
                data    : me.data,
                listeners,
                emit,
                element : refElement.value,
                processCellContent,
                hasFrameworkRenderer
            });
            relayStores(me.data, instance.value);

            const watcher = (prop, newValue) => watch(() => me.props[prop], newValue);
            watchProps(me, instance.value, me.props, me.data, watcher);
        });

        onBeforeUnmount(() => {
            if (instance.value) {
                instance.value.destroy();
            }
        });

        return { instance, refElement, teleports };
    }
};
</script>
